import {Control, Controller, useFormState, UseFormWatch} from "react-hook-form";
import useDebounce from "../../../hooks/use-debounce";
import {
  useLegacyDisplayNameStatus,
  useLegacyVerifyDisplayName,
} from "../../../features/profile/hooks/useProfile";
import {CustomInputComponent} from "../../../components/input/input-component";
import {IEditProfileFormValues} from "../../../components/profile/edit-profile/edit-profile";
import {
  FormField,
  FormFieldError,
  FormFieldHelperText,
} from "../../../design-system/form-field";
import {Input, Space, Tooltip} from "antd";
import {CheckOutlined, LoadingOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import {useEffect} from "react";

export function OnboardingPublicDisplayNameFormField({
  control,
  watch,
  error,
  setError,
  clearErrors,
  firstName,
  lastName,
}: {
  control: Control<IEditProfileFormValues, any>;
  watch: UseFormWatch<IEditProfileFormValues>;
  error?: any;
  setError: any;
  clearErrors: any;
  firstName?: string;
  lastName?: string;
}) {
  const {dirtyFields} = useFormState({control, name: "displayName"});
  const isDisplayNameDirty = !!dirtyFields.displayName;

  const displayName = watch("displayName");
  const debouncedDisplayName = useDebounce(displayName, 2000);

  const {
    isSuccess,
    error: queryError,
    data: verifyData,
    isFetching,
  } = useLegacyVerifyDisplayName(debouncedDisplayName, {enabled: isDisplayNameDirty});

  const isDisplayNameAvailable = isSuccess && verifyData?.isAvailable !== false;

  useEffect(() => {
    if (queryError) {
      setError("displayName", {
        type: "manual",
        message: queryError.message,
      });
    } else if (!isDisplayNameAvailable && isSuccess) {
      setError("displayName", {
        type: "manual",
        message: "Display name is already taken",
      });
    } else if (isDisplayNameAvailable) {
      clearErrors("displayName");
    }
  }, [clearErrors, isDisplayNameAvailable, isSuccess, queryError, setError]);

  const {data: displayNameStatus} = useLegacyDisplayNameStatus();
  const isDisabled = displayNameStatus?.isAvailable === false;

  const defaultName = firstName && lastName ? `${firstName} ${lastName}` : null;

  return (
    <>
      <FormField
        id="displayName"
        label="Public display name"
        infoTip={() => (
          <span style={{margin: "0px 6px"}}>
            <Tooltip
              placement="rightTop"
              title="Display name is shown on your public profile and offerings. You can only change it once every 14 days. Need to update it sooner? Contact support: hello@coachli.co"
            >
              <QuestionCircleOutlined />
            </Tooltip>
          </span>
        )}
        renderInput={(id) => (
          <Controller
            control={control}
            name="displayName"
            render={({field: {onChange, value, ref, onBlur}, fieldState: {error}}) => (
              <Space size={8} direction="vertical" style={{display: "flex"}}>
                <Space size={4} direction="vertical" style={{display: "flex"}}>
                  <Input
                    id={id}
                    status={error?.message ? "error" : ""}
                    onChange={onChange}
                    onBlur={onBlur}
                    size="large"
                    value={value === null ? undefined : value}
                    placeholder={defaultName || "John Doe"}
                    ref={ref}
                    disabled={isDisabled}
                    suffix={
                      isFetching ? (
                        <div>
                          <LoadingOutlined style={{fontSize: 24}} spin />
                        </div>
                      ) : isDisplayNameAvailable ? (
                        <div>
                          <CheckOutlined />
                        </div>
                      ) : null
                    }
                  />
                </Space>
                <FormFieldHelperText text="Your public display name must be yours • 1 change every 14 days" />
                <FormFieldError errorMessage={error?.message || queryError?.message} />
              </Space>
            )}
          />
        )}
      />
    </>
  );
}
